/**
 * Multi-step project submission wizard.
 *
 * Guides users through choosing project type, describing their idea,
 * adding details, and reviewing before submission. Unauthenticated
 * visitors get an inline register/login step before the final submit.
 *
 * @module SubmitProjectPage
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Stack,
  alpha,
  StepConnector,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  LinearProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LanguageIcon from '@mui/icons-material/Language';
import DevicesIcon from '@mui/icons-material/Devices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TuneIcon from '@mui/icons-material/Tune';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PaletteIcon from '@mui/icons-material/Palette';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api, { getErrorMessage } from '../api';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FloatingOrb } from '../components/AnimatedComponents';

/**
 * Score a password on a 0–100 scale based on length, uppercase,
 * digits, and special characters.
 */
function getPasswordStrength(pwd: string): { score: number; label: string; color: string } {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { score: 20, label: 'Weak', color: '#f44336' };
  if (score <= 2) return { score: 40, label: 'Fair', color: '#ff9800' };
  if (score <= 3) return { score: 60, label: 'Good', color: '#ffc107' };
  if (score <= 4) return { score: 80, label: 'Strong', color: GREEN };
  return { score: 100, label: 'Very Strong', color: '#2d9c1c' };
}

/** Framer Motion slide transition variants for the multi-step form. */
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0,
  }),
};

/** The project submission wizard with stepper navigation and animated step transitions. */
export default function SubmitProjectPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const baseSteps = [
    { label: t('submit.stepType'), icon: <AssignmentIcon /> },
    { label: t('submit.stepIdea'), icon: <LightbulbIcon /> },
    { label: t('submit.stepDetails'), icon: <TuneIcon /> },
    { label: t('submit.stepReview'), icon: <RateReviewIcon /> },
  ];

  const authStep = { label: t('submit.stepAuth'), icon: <LoginIcon /> };

  const strengthLabels: Record<string, string> = {
    'Weak': t('register.strengthWeak'),
    'Fair': t('register.strengthFair'),
    'Good': t('register.strengthGood'),
    'Strong': t('register.strengthStrong'),
    'Very Strong': t('register.strengthVeryStrong'),
  };

  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    project_type: '',
    app_name: '',
    description: '',
    target_audience: '',
    key_features: [''] as string[],
    ai_features: [''] as string[],
    design_preferences: ['#1a3a5c', '#39b54a'] as string[],
    timeline: '',
    deadline: '',
    budget_range: '',
    additional_notes: '',
  });

  // Logo state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [extractingColors, setExtractingColors] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const colorInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auth form state
  const [authTab, setAuthTab] = useState(0); // 0 = Sign In, 1 = Create Account
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    company: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  // Dynamic steps: if user is already logged in, skip the auth step
  const steps = user ? [...baseSteps.slice(0, 3), { label: t('submit.stepReviewSubmit'), icon: <RateReviewIcon /> }] : [...baseSteps, authStep];
  const reviewStepIndex = 3;
  const authStepIndex = user ? -1 : 4; // -1 means no auth step needed

  const handleChange = (field: string) => (e: any) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // --- List field handlers (key_features, ai_features) ---
  const handleListChange = (field: 'key_features' | 'ai_features', index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => {
      const list = [...prev[field]];
      list[index] = e.target.value;
      return { ...prev, [field]: list };
    });
  };

  const addListItem = (field: 'key_features' | 'ai_features') => () => {
    setForm((prev) => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeListItem = (field: 'key_features' | 'ai_features', index: number) => () => {
    setForm((prev) => {
      const list = prev[field].filter((_: string, i: number) => i !== index);
      return { ...prev, [field]: list.length ? list : [''] };
    });
  };

  // --- Color handlers ---
  const handleColorChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => {
      const colors = [...prev.design_preferences];
      colors[index] = e.target.value;
      return { ...prev, design_preferences: colors };
    });
  };

  const addColor = () => {
    if (form.design_preferences.length < 5) {
      setForm((prev) => ({ ...prev, design_preferences: [...prev.design_preferences, '#cccccc'] }));
    }
  };

  const removeColor = (index: number) => () => {
    if (form.design_preferences.length > 2) {
      setForm((prev) => ({
        ...prev,
        design_preferences: prev.design_preferences.filter((_: string, i: number) => i !== index),
      }));
    }
  };

  // --- Logo upload & color extraction ---
  const extractColorsFromImage = (imageUrl: string): Promise<string[]> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 100;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve([]); return; }
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;

        // Build a frequency map of colors (quantized to reduce noise)
        const colorMap = new Map<string, number>();
        for (let i = 0; i < data.length; i += 4) {
          const r = Math.round(data[i] / 16) * 16;
          const g = Math.round(data[i + 1] / 16) * 16;
          const b = Math.round(data[i + 2] / 16) * 16;
          const a = data[i + 3];
          if (a < 128) continue; // skip transparent
          // Skip near-white and near-black
          if (r > 240 && g > 240 && b > 240) continue;
          if (r < 15 && g < 15 && b < 15) continue;
          const hex = '#' + [r, g, b].map(c => Math.min(255, c).toString(16).padStart(2, '0')).join('');
          colorMap.set(hex, (colorMap.get(hex) || 0) + 1);
        }

        // Sort by frequency, pick top distinct colors
        const sorted = [...colorMap.entries()].sort((a, b) => b[1] - a[1]);
        const distinct: string[] = [];
        for (const [hex] of sorted) {
          if (distinct.length >= 5) break;
          // Ensure colors are visually distinct (Euclidean distance in RGB)
          const isDifferent = distinct.every(existing => {
            const r1 = parseInt(hex.slice(1, 3), 16), g1 = parseInt(hex.slice(3, 5), 16), b1 = parseInt(hex.slice(5, 7), 16);
            const r2 = parseInt(existing.slice(1, 3), 16), g2 = parseInt(existing.slice(3, 5), 16), b2 = parseInt(existing.slice(5, 7), 16);
            return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2) > 60;
          });
          if (isDifferent) distinct.push(hex);
        }
        // Ensure at least 2 colors
        while (distinct.length < 2 && sorted.length > distinct.length) {
          const next = sorted[distinct.length]?.[0];
          if (next && !distinct.includes(next)) distinct.push(next);
          else break;
        }
        resolve(distinct.length >= 2 ? distinct : []);
      };
      img.onerror = () => resolve([]);
      img.src = imageUrl;
    });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'];
    const ext = file.name.toLowerCase().split('.').pop() || '';
    if (!allowedTypes.includes(file.type) && !['svg', 'png', 'jpg', 'jpeg'].includes(ext)) {
      setError(t('submit.logoInvalidType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('submit.logoTooLarge'));
      return;
    }

    setError('');
    setLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);

    // Extract colors from the image (not for SVG - canvas can't always render SVG well)
    if (ext !== 'svg' && file.type !== 'image/svg+xml') {
      setExtractingColors(true);
      const colors = await extractColorsFromImage(previewUrl);
      if (colors.length >= 2) {
        setForm((prev) => ({ ...prev, design_preferences: colors }));
      }
      setExtractingColors(false);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  // --- Intelligent timeline computation ---
  const getTimelineOptions = (): { value: string; label: string; minLabel: string }[] => {
    const projectType = form.project_type;
    const hasAi = form.ai_features.some(f => f.trim() !== '');
    const aiCount = form.ai_features.filter(f => f.trim() !== '').length;
    const isBoth = projectType === 'both';

    // Website OR app without AI: min 1-2 weeks
    // Website OR app with 1 AI feature: min 3-4 weeks
    // Website AND app: min 3-4 weeks
    // Anything above: min 1-2 months
    let minWeeks = '1-2';
    let minLabel = t('submit.timelineMin12Weeks');

    if (isBoth && hasAi) {
      minWeeks = '1-2 months';
      minLabel = t('submit.timelineMin12Months');
    } else if (isBoth || (hasAi && aiCount >= 2)) {
      minWeeks = '3-4';
      minLabel = t('submit.timelineMin34Weeks');
    } else if (hasAi && aiCount === 1) {
      minWeeks = '3-4';
      minLabel = t('submit.timelineMin34Weeks');
    }

    if (minWeeks === '1-2 months') {
      return [
        { value: '1-2 months', label: t('submit.timeline12Months'), minLabel },
        { value: '2-3 months', label: t('submit.timeline3'), minLabel: '' },
        { value: '3-6 months', label: t('submit.timeline4'), minLabel: '' },
        { value: 'flexible', label: t('submit.timeline5'), minLabel: '' },
      ];
    }
    if (minWeeks === '3-4') {
      return [
        { value: '3-4 weeks', label: t('submit.timeline34Weeks'), minLabel },
        { value: '1-2 months', label: t('submit.timeline12Months'), minLabel: '' },
        { value: '2-3 months', label: t('submit.timeline3'), minLabel: '' },
        { value: '3-6 months', label: t('submit.timeline4'), minLabel: '' },
        { value: 'flexible', label: t('submit.timeline5'), minLabel: '' },
      ];
    }
    return [
      { value: '1-2 weeks', label: t('submit.timeline1'), minLabel },
      { value: '3-4 weeks', label: t('submit.timeline34Weeks'), minLabel: '' },
      { value: '1-2 months', label: t('submit.timeline12Months'), minLabel: '' },
      { value: '2-3 months', label: t('submit.timeline3'), minLabel: '' },
      { value: '3-6 months', label: t('submit.timeline4'), minLabel: '' },
      { value: 'flexible', label: t('submit.timeline5'), minLabel: '' },
    ];
  };

  const handleAuthChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setAuthForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const canNext = () => {
    switch (activeStep) {
      case 0:
        return !!form.project_type;
      case 1:
        return !!form.app_name && !!form.description;
      case 2:
        return true;
      case 3: // Review step — always allow next (to auth step) or submit
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    setDirection(1);
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setDirection(-1);
    setActiveStep((s) => s - 1);
  };

  const submitProject = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      // Filter empty items from lists
      const payload = {
        ...form,
        key_features: form.key_features.filter(f => f.trim()),
        ai_features: form.ai_features.filter(f => f.trim()),
        design_preferences: form.design_preferences,
        deadline: form.deadline || null,
      };
      if (payload.key_features.length === 0) payload.key_features = null as any;
      if (payload.ai_features.length === 0) payload.ai_features = null as any;

      const resp = await api.post('/api/projects/', payload);
      const projectId = resp.data.id;

      // Upload logo if provided
      if (logoFile && projectId) {
        const formData = new FormData();
        formData.append('file', logoFile);
        try {
          await api.post(`/api/projects/${projectId}/logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          // Logo upload failure is non-critical
        }
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(getErrorMessage(err, t('submit.error')));
    } finally {
      setLoading(false);
    }
  }, [form, logoFile, navigate, t]);

  const handleAuthAndSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      if (authTab === 0) {
        // Sign in
        await login(authForm.email, authForm.password);
      } else {
        // Register
        if (authForm.password !== authForm.confirmPassword) {
          setError(t('register.errorMismatch'));
          setLoading(false);
          return;
        }
        if (authForm.password.length < 8) {
          setError(t('register.errorMinLength'));
          setLoading(false);
          return;
        }
        await register(authForm.email, authForm.password, authForm.fullName, authForm.company || undefined);
      }
      // Auth succeeded — now submit the project
      const payload = {
        ...form,
        key_features: form.key_features.filter(f => f.trim()),
        ai_features: form.ai_features.filter(f => f.trim()),
        design_preferences: form.design_preferences,
        deadline: form.deadline || null,
      };
      if (payload.key_features.length === 0) payload.key_features = null as any;
      if (payload.ai_features.length === 0) payload.ai_features = null as any;

      const resp = await api.post('/api/projects/', payload);
      const projectId = resp.data.id;

      if (logoFile && projectId) {
        const formData = new FormData();
        formData.append('file', logoFile);
        try {
          await api.post(`/api/projects/${projectId}/logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          // Logo upload failure is non-critical
        }
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(getErrorMessage(err, t('submit.authError')));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (user) {
      await submitProject();
    } else {
      // Should not happen — non-auth users go to auth step
      handleNext();
    }
  };

  const strength = getPasswordStrength(authForm.password);

  return (
    <PageTransition>
      <SEO
        title={t('submit.seoTitle')}
        description={t('submit.seoDesc')}
        canonical="/submit-project"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: `${t('submit.seoTitle')} — ${t('seo.siteName')}`,
          description: t('submit.seoDesc'),
          url: 'https://www.booge-it.com/submit-project',
        }}
      />
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${alpha(GREEN, 0.02)} 0%, ${alpha(DARK_BLUE, 0.02)} 100%)`,
        }}
      >
        <FloatingOrb size={300} color={GREEN} top={-80} right={-60} />
        <FloatingOrb size={200} color={DARK_BLUE} bottom={-40} left={-40} animationDelay="4s" />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h3"
              align="center"
              sx={{
                mb: 1,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('submit.title')}
            </Typography>
            <Typography align="center" color="text.secondary" sx={{ mb: 5, fontSize: '1.05rem' }}>
              {t('submit.subtitle')}
            </Typography>
          </motion.div>

          <Stepper
            activeStep={activeStep}
            sx={{ mb: 5 }}
            alternativeLabel
            connector={
              <StepConnector
                sx={{
                  '& .MuiStepConnector-line': {
                    borderColor: alpha(GREEN, 0.2),
                    borderTopWidth: 3,
                    borderRadius: 2,
                  },
                  '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
                    borderColor: GREEN,
                  },
                }}
              />
            }
          >
            {steps.map((step, idx) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: idx <= activeStep
                          ? `linear-gradient(135deg, ${GREEN}, #2d9c1c)`
                          : alpha(DARK_BLUE, 0.08),
                        color: idx <= activeStep ? 'white' : 'text.secondary',
                        transition: 'all 0.4s ease',
                        boxShadow: idx <= activeStep ? `0 4px 15px ${alpha(GREEN, 0.3)}` : 'none',
                      }}
                    >
                      {React.cloneElement(step.icon, { sx: { fontSize: 22 } })}
                    </Box>
                  )}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: idx <= activeStep ? 600 : 400,
                      color: idx <= activeStep ? DARK_BLUE : 'text.secondary',
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            </motion.div>
          )}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              minHeight: 320,
              boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
              border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${GREEN}, ${DARK_BLUE})` }} />

            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={activeStep}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {/* Step 1: Project Type */}
                {activeStep === 0 && (
                  <Box>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                      {t('submit.typeTitle')}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {[
                        { value: 'app', label: t('submit.typeApp'), icon: <PhoneAndroidIcon />, desc: t('submit.typeAppDesc') },
                        { value: 'website', label: t('submit.typeWebsite'), icon: <LanguageIcon />, desc: t('submit.typeWebsiteDesc') },
                        { value: 'both', label: t('submit.typeBoth'), icon: <DevicesIcon />, desc: t('submit.typeBothDesc') },
                      ].map((opt) => (
                        <Paper
                          key={opt.value}
                          onClick={() => setForm((prev) => ({ ...prev, project_type: opt.value }))}
                          sx={{
                            flex: 1,
                            p: 3,
                            cursor: 'pointer',
                            textAlign: 'center',
                            border: 2,
                            borderColor: form.project_type === opt.value ? GREEN : 'transparent',
                            bgcolor: form.project_type === opt.value ? alpha(GREEN, 0.05) : 'background.default',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              borderColor: alpha(GREEN, 0.5),
                              transform: 'translateY(-4px)',
                              boxShadow: `0 8px 25px ${alpha(GREEN, 0.15)}`,
                            },
                          }}
                          elevation={form.project_type === opt.value ? 4 : 1}
                        >
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 1.5,
                              background: form.project_type === opt.value
                                ? `linear-gradient(135deg, ${GREEN}, #2d9c1c)`
                                : alpha(DARK_BLUE, 0.08),
                              color: form.project_type === opt.value ? 'white' : 'text.secondary',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {React.cloneElement(opt.icon, { sx: { fontSize: 28 } })}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                          <Typography variant="body2" color="text.secondary">{opt.desc}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                    {form.project_type && (form.project_type === 'app' || form.project_type === 'both') && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                        <Box sx={{ mt: 3, p: 2, bgcolor: alpha(GREEN, 0.05), borderRadius: 3, border: `1px solid ${alpha(GREEN, 0.15)}` }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('submit.platformsAvailable')}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {['Android', 'iOS', 'Windows', 'macOS', 'Linux'].map((p) => (
                              <Chip key={p} label={p} size="small" color="secondary" variant="outlined" />
                            ))}
                          </Stack>
                        </Box>
                      </motion.div>
                    )}
                  </Box>
                )}

                {/* Step 2: Describe Idea */}
                {activeStep === 1 && (
                  <Box>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                      {t('submit.ideaTitle')}
                    </Typography>
                    <TextField
                      fullWidth
                      label={t('submit.appName')}
                      value={form.app_name}
                      onChange={handleChange('app_name')}
                      required
                      sx={{ mb: 3 }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label={t('submit.description')}
                      helperText={t('submit.descriptionHelp')}
                      value={form.description}
                      onChange={handleChange('description')}
                      required
                      sx={{ mb: 3 }}
                    />
                    <TextField
                      fullWidth
                      label={t('submit.targetAudience')}
                      helperText={t('submit.targetAudienceHelp')}
                      value={form.target_audience}
                      onChange={handleChange('target_audience')}
                    />
                  </Box>
                )}

                {/* Step 3: Details */}
                {activeStep === 2 && (
                  <Box>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                      {t('submit.detailsTitle')}
                    </Typography>

                    {/* Key Features — dynamic list */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('submit.keyFeatures')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t('submit.keyFeaturesHelp')}
                    </Typography>
                    {form.key_features.map((feature, idx) => (
                      <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={`${t('submit.featurePlaceholder')} ${idx + 1}`}
                          value={feature}
                          onChange={handleListChange('key_features', idx)}
                        />
                        <IconButton
                          size="small"
                          onClick={removeListItem('key_features', idx)}
                          disabled={form.key_features.length <= 1 && !feature}
                          sx={{ color: 'text.secondary' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addListItem('key_features')}
                      sx={{ mb: 3, textTransform: 'none' }}
                    >
                      {t('submit.addFeature')}
                    </Button>

                    <Divider sx={{ mb: 3 }} />

                    {/* AI Features — dynamic list */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('submit.aiFeatures')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t('submit.aiFeaturesHelp')}
                    </Typography>
                    {form.ai_features.map((feature, idx) => (
                      <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={`${t('submit.aiFeaturePlaceholder')} ${idx + 1}`}
                          value={feature}
                          onChange={handleListChange('ai_features', idx)}
                        />
                        <IconButton
                          size="small"
                          onClick={removeListItem('ai_features', idx)}
                          disabled={form.ai_features.length <= 1 && !feature}
                          sx={{ color: 'text.secondary' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addListItem('ai_features')}
                      sx={{ mb: 3, textTransform: 'none' }}
                    >
                      {t('submit.addAiFeature')}
                    </Button>

                    <Divider sx={{ mb: 3 }} />

                    {/* Logo Upload */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('submit.logoUpload')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t('submit.logoUploadHelp')}
                    </Typography>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept=".svg,.png,.jpg,.jpeg"
                      style={{ display: 'none' }}
                      onChange={handleLogoUpload}
                    />
                    {logoPreview ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            border: `2px solid ${alpha(GREEN, 0.3)}`,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.default',
                          }}
                        >
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{logoFile?.name}</Typography>
                          <Button size="small" color="error" onClick={removeLogo} sx={{ textTransform: 'none', mt: 0.5 }}>
                            {t('submit.removeLogo')}
                          </Button>
                        </Box>
                        {extractingColors && (
                          <Chip label={t('submit.extractingColors')} size="small" color="secondary" />
                        )}
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => logoInputRef.current?.click()}
                        sx={{ mb: 3, textTransform: 'none', borderRadius: 2 }}
                      >
                        {t('submit.selectLogo')}
                      </Button>
                    )}

                    <Divider sx={{ mb: 3 }} />

                    {/* Design Preferences — Color picker */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaletteIcon fontSize="small" />
                      {t('submit.designPreferences')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('submit.designPreferencesHelp')}
                    </Typography>
                    <Stack spacing={1.5} sx={{ mb: 1 }}>
                      {form.design_preferences.map((color, idx) => (
                        <Stack key={idx} direction="row" alignItems="center" spacing={1.5}>
                          <Tooltip title={t('submit.clickToPickColor')}>
                            <Box
                              onClick={() => colorInputRefs.current[idx]?.click()}
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                bgcolor: color,
                                border: '2px solid',
                                borderColor: alpha('#000', 0.12),
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'scale(1.1)' },
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                          <input
                            ref={el => { colorInputRefs.current[idx] = el; }}
                            type="color"
                            value={color}
                            onChange={handleColorChange(idx)}
                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                          />
                          <TextField
                            size="small"
                            value={color}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^#[0-9a-fA-F]{0,6}$/.test(val) || val === '') {
                                handleColorChange(idx)({ target: { value: val } } as any);
                              }
                            }}
                            sx={{ width: 130 }}
                            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                            {(() => {
                              const r = parseInt(color.slice(1, 3), 16) || 0;
                              const g = parseInt(color.slice(3, 5), 16) || 0;
                              const b = parseInt(color.slice(5, 7), 16) || 0;
                              return `RGB(${r}, ${g}, ${b})`;
                            })()}
                          </Typography>
                          {form.design_preferences.length > 2 && (
                            <IconButton size="small" onClick={removeColor(idx)} sx={{ color: 'text.secondary' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                    {form.design_preferences.length < 5 && (
                      <Button size="small" startIcon={<AddIcon />} onClick={addColor} sx={{ mb: 3, textTransform: 'none' }}>
                        {t('submit.addColor')}
                      </Button>
                    )}
                    {form.design_preferences.length >= 5 && <Box sx={{ mb: 3 }} />}

                    <Divider sx={{ mb: 3 }} />

                    {/* Timeline — intelligent */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('submit.timeline')}
                    </Typography>
                    {getTimelineOptions()[0]?.minLabel && (
                      <Alert severity="info" sx={{ mb: 2 }} icon={false}>
                        {getTimelineOptions()[0].minLabel}
                      </Alert>
                    )}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>{t('submit.timeline')}</InputLabel>
                      <Select
                        value={form.timeline}
                        label={t('submit.timeline')}
                        onChange={handleChange('timeline')}
                      >
                        {getTimelineOptions().map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Deadline date picker */}
                    <TextField
                      fullWidth
                      label={t('submit.deadline')}
                      helperText={t('submit.deadlineHelp')}
                      type="date"
                      value={form.deadline}
                      onChange={handleChange('deadline')}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 3 }}
                    />

                    <Divider sx={{ mb: 3 }} />

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>{t('submit.budget')}</InputLabel>
                      <Select
                        value={form.budget_range}
                        label={t('submit.budget')}
                        onChange={handleChange('budget_range')}
                      >
                        <MenuItem value="< €5,000">{t('submit.budget1')}</MenuItem>
                        <MenuItem value="€5,000 - €15,000">{t('submit.budget2')}</MenuItem>
                        <MenuItem value="€15,000 - €50,000">{t('submit.budget3')}</MenuItem>
                        <MenuItem value="> €50,000">{t('submit.budget4')}</MenuItem>
                        <MenuItem value="discuss">{t('submit.budget5')}</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label={t('submit.additionalNotes')}
                      value={form.additional_notes}
                      onChange={handleChange('additional_notes')}
                    />
                  </Box>
                )}

                {/* Step 4: Review */}
                {activeStep === 3 && (
                  <Box>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                      {t('submit.reviewTitle')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {[
                        { label: t('submit.reviewType'), value: form.project_type === 'both' ? t('submit.typeBoth') : form.project_type === 'app' ? t('submit.typeApp') : t('submit.typeWebsite') },
                        { label: t('submit.reviewName'), value: form.app_name },
                        { label: t('submit.reviewDescription'), value: form.description },
                        { label: t('submit.reviewAudience'), value: form.target_audience },
                        { label: t('submit.reviewFeatures'), value: form.key_features.filter(f => f.trim()).join(', ') },
                        { label: t('submit.reviewAi'), value: form.ai_features.filter(f => f.trim()).join(', ') },
                        { label: t('submit.reviewTimeline'), value: form.timeline },
                        { label: t('submit.reviewDeadline'), value: form.deadline },
                        { label: t('submit.reviewBudget'), value: form.budget_range },
                        { label: t('submit.reviewNotes'), value: form.additional_notes },
                      ]
                        .filter((item) => item.value)
                        .map((item) => (
                          <Box
                            key={item.label}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: alpha(DARK_BLUE, 0.02),
                              border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                            }}
                          >
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              {item.label}
                            </Typography>
                            <Typography sx={{ fontWeight: 500 }}>{item.value}</Typography>
                          </Box>
                        ))}

                      {/* Design colors review */}
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(DARK_BLUE, 0.02), border: `1px solid ${alpha(DARK_BLUE, 0.06)}` }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                          {t('submit.reviewDesign')}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {form.design_preferences.map((color, idx) => (
                            <Tooltip key={idx} title={color}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1.5,
                                  bgcolor: color,
                                  border: '2px solid',
                                  borderColor: alpha('#000', 0.12),
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      </Box>

                      {/* Logo review */}
                      {logoPreview && (
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(DARK_BLUE, 0.02), border: `1px solid ${alpha(DARK_BLUE, 0.06)}` }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                            {t('submit.reviewLogo')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 48, height: 48, borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
                              <img src={logoPreview} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{logoFile?.name}</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    {(form.project_type === 'app' || form.project_type === 'both') && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: alpha(GREEN, 0.05), borderRadius: 3, border: `1px solid ${alpha(GREEN, 0.15)}` }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('submit.reviewPlatforms')}
                        </Typography>
                      </Box>
                    )}
                    {!user && (
                      <Box sx={{ mt: 3, p: 2, bgcolor: alpha(GREEN, 0.04), borderRadius: 3, border: `1px solid ${alpha(GREEN, 0.12)}` }}>
                        <Typography variant="body2" color="text.secondary">
                          <CheckCircleOutlineIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5, color: GREEN }} />
                          {t('submit.reviewAuthHint')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Step 5: Auth (only for non-authenticated users) */}
                {activeStep === 4 && !user && (
                  <Box>
                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('submit.authTitle')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3, fontSize: '0.95rem' }}>
                      {t('submit.authSubtitle')}
                    </Typography>

                    <Tabs
                      value={authTab}
                      onChange={(_, v) => { setAuthTab(v); setError(''); }}
                      sx={{
                        mb: 3,
                        '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' },
                        '& .MuiTabs-indicator': { backgroundColor: GREEN, height: 3, borderRadius: 2 },
                      }}
                    >
                      <Tab label={t('submit.authSignIn')} />
                      <Tab label={t('submit.authCreateAccount')} />
                    </Tabs>

                    {authTab === 0 ? (
                      /* Sign In Form */
                      <Box>
                        <TextField
                          fullWidth
                          label={t('login.email')}
                          type="email"
                          value={authForm.email}
                          onChange={handleAuthChange('email')}
                          required
                          sx={{ mb: 2.5 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          fullWidth
                          label={t('login.password')}
                          type={showPassword ? 'text' : 'password'}
                          value={authForm.password}
                          onChange={handleAuthChange('password')}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    ) : (
                      /* Register Form */
                      <Box>
                        <TextField
                          fullWidth
                          label={t('register.fullName')}
                          value={authForm.fullName}
                          onChange={handleAuthChange('fullName')}
                          required
                          sx={{ mb: 2.5 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          fullWidth
                          label={t('login.email')}
                          type="email"
                          value={authForm.email}
                          onChange={handleAuthChange('email')}
                          required
                          sx={{ mb: 2.5 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          fullWidth
                          label={t('register.company')}
                          value={authForm.company}
                          onChange={handleAuthChange('company')}
                          sx={{ mb: 2.5 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          fullWidth
                          label={t('login.password')}
                          type={showPassword ? 'text' : 'password'}
                          value={authForm.password}
                          onChange={handleAuthChange('password')}
                          required
                          sx={{ mb: 1 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {authForm.password && (
                          <Box sx={{ mb: 2.5 }}>
                            <LinearProgress
                              variant="determinate"
                              value={strength.score}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: alpha(DARK_BLUE, 0.08),
                                '& .MuiLinearProgress-bar': { bgcolor: strength.color, borderRadius: 3 },
                              }}
                            />
                            <Typography variant="caption" sx={{ color: strength.color, fontWeight: 600 }}>
                              {strengthLabels[strength.label] || strength.label}
                            </Typography>
                          </Box>
                        )}
                        <TextField
                          fullWidth
                          label={t('register.confirmPassword')}
                          type={showPassword ? 'text' : 'password'}
                          value={authForm.confirmPassword}
                          onChange={handleAuthChange('confirmPassword')}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </motion.div>
            </AnimatePresence>
          </Paper>

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{ borderRadius: 3, px: 3 }}
            >
              {t('submit.back')}
            </Button>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                color="secondary"
                disabled={!canNext()}
                onClick={activeStep === reviewStepIndex && user ? handleSubmit : handleNext}
                endIcon={activeStep === reviewStepIndex && user ? <SendIcon /> : <ArrowForwardIcon />}
                sx={{ borderRadius: 3, px: 4 }}
              >
                {activeStep === reviewStepIndex && user
                  ? (loading ? t('submit.submitting') : t('submit.submitProject'))
                  : t('submit.next')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={user ? handleSubmit : handleAuthAndSubmit}
                disabled={loading}
                endIcon={<SendIcon />}
                sx={{ borderRadius: 3, px: 4 }}
              >
                {loading ? t('submit.submitting') : t('submit.signInSubmit')}
              </Button>
            )}
          </Box>
        </Container>
      </Box>
    </PageTransition>
  );
}
